import type { ReaderHost } from './controller.js';

/**
 * DOM implementation of the ReaderHost seam. Wake Lock is feature-detected
 * and playback-only (PD-15): acquired on play, released on pause/dispose;
 * the browser itself releases it when the tab hides, which is fine because
 * PD-15 pauses playback then anyway.
 *
 * W-2 (Sol M-07): navigator.wakeLock.request() is async — a Pause landing
 * while the request is in flight used to leave the late-resolving sentinel
 * HELD with playback stopped (the screen stayed awake indefinitely). The
 * host now tracks the DESIRED awake state; a sentinel that lands when awake
 * is no longer desired releases itself immediately.
 */
export function createBrowserHost(): ReaderHost {
  let wakeLock: { release(): Promise<void> } | null = null;
  let acquiring = false;
  // The desired-awake flag: true from acquire until the matching release.
  // Every resolved sentinel is checked against it before being adopted.
  let desired = false;

  return {
    requestFrame: (callback) => window.requestAnimationFrame(() => callback()),
    cancelFrame: (handle) => window.cancelAnimationFrame(handle),
    setTimer: (callback, ms) => window.setTimeout(callback, ms),
    clearTimer: (handle) => window.clearTimeout(handle),
    now: () => performance.now(),
    acquireWakeLock: () => {
      const nav = navigator as Navigator & {
        wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> };
      };
      desired = true;
      if (nav.wakeLock === undefined || wakeLock !== null || acquiring) return;
      acquiring = true;
      nav.wakeLock
        .request('screen')
        .then((sentinel) => {
          if (!desired) {
            // Pause won the race: the sentinel resolved after playback stopped
            // — release it immediately, never hold the screen while paused.
            void sentinel.release().catch(() => undefined);
            return;
          }
          wakeLock = sentinel;
        })
        .catch(() => undefined)
        .finally(() => {
          acquiring = false;
        });
    },
    releaseWakeLock: () => {
      desired = false;
      void wakeLock?.release().catch(() => undefined);
      wakeLock = null;
    },
  };
}
